'use client';

import Image from 'next/image';
import { careerPath } from '@/static/career_path';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { CareerPathVersion } from '@/types/career-path';

interface CareerPathContentProps {
  version: CareerPathVersion;
  variant: string;
}

const CareerPathContent: React.FC<CareerPathContentProps> = ({ version }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="carrer_path" className="py-16">
      <div className="max-w-8xl mx-auto px-5 md:px-[50px] lg:px-5">
        <h2 className="mb-16 text-3xl font-bold lg:max-w-3xl lg:text-5xl lg:leading-tight">
          {version.title}
          <br />
          <span className="text-[#9447FF]">{version.titleColor}</span>
        </h2>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
          {version.bullets.map((bullet, index) => (
            <div
              key={index}
              className={`flex flex-col gap-4 text-center transition-all duration-1000 ease-out md:flex-row md:items-center md:text-center lg:block lg:gap-0 ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-16 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="relative overflow-hidden rounded-[48px] md:flex-1 lg:mb-6">
                <Image
                  src={bullet.image}
                  alt={bullet.title}
                  width={500}
                  height={400}
                  className="h-auto w-full rounded-[48px] object-contain"
                  loading="lazy"
                  sizes="(min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                />
              </div>
              <div className="md:flex-1 lg:flex-none">
                <h3
                  dangerouslySetInnerHTML={{ __html: bullet.title }}
                  className="mb-4 text-lg font-semibold text-gray-800 md:text-xl lg:text-2xl"
                />
                <p className="text-sm leading-[1.5] text-gray-600 md:text-base">
                  {bullet.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareerPath: React.FC = () => {
  const searchParams = useSearchParams();
  const urlVersion = searchParams?.get('version')?.toUpperCase() || 'A';
  const variant = urlVersion === 'A' || urlVersion === 'B' ? urlVersion : 'A';
  const versionKey = `version${variant}` as 'versionA' | 'versionB';
  const version = careerPath[0][versionKey];

  if (!version) {
    console.error(`Version ${variant} not found in careerPath data`);

    return null;
  }

  return (
    <CareerPathContent key={variant} version={version} variant={variant} />
  );
};

export default CareerPath;
